import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { BookLink } from '@todo/web-ui/components/book-link';
import { DetailLayout } from '@todo/web-ui/components/detail-layout';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { localizedBoatDescription, localizedBoatSummary, localizedBoatTitle, localizedBoatType } from '@todo/web-ui/lib/boat-content';
import { formatPrice, localePath, partnerName } from '@todo/web-ui/lib/format';
import { isBoatDelisted, type Boat } from '@todo/core/boats';
import { useBoat } from '@todo/core/hooks/use-boat';
import { useLocale } from '@todo/core/i18n/use-locale';
import { boatsCrumb, breadcrumbJsonLd, seoTitle, truncate } from '@todo/core/seo';
import { getPrefetched, prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';

// One page per boat (delisted ones too, for their 30 days — see
// Boat.delisted_at), prerendered from @todo/core's build-time fetch.
export { generateBoatStaticParams as generateStaticParams } from '@todo/core/detail/boat-detail';

export default function BoatPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { boat: fetched, loading } = useBoat(id);
  const boat = fetched ?? getPrefetched<Boat>('boat', id) ?? null;

  if (!boat) return loading ? <Page>{null}</Page> : <NotFoundScreen />;
  return <BoatScreen boat={boat} />;
}

function BoatScreen({ boat }: { boat: Boat }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const title = localizedBoatTitle(boat, locale);
  const description = localizedBoatDescription(boat, locale);
  const delisted = isBoatDelisted(boat);
  const type = localizedBoatType(boat, locale);
  const partner = partnerName(boat.source);

  const facts: { label: string; value: string }[] = [];
  if (boat.year) facts.push({ label: t('detail.year'), value: String(boat.year) });
  if (boat.from_price != null) {
    facts.push({
      label: t('detail.price'),
      value: `${t('card.from', { price: formatPrice(Number(boat.from_price), boat.currency, locale) })} ${t('boats.perDay')}`,
    });
  }

  return (
    <Page>
      <PageHead
        title={seoTitle(title)}
        description={truncate(localizedBoatSummary(boat, locale))}
        path={`boats/${boat.id}`}
        image={boat.image_url}
        // Not indexed yet (no Click&Boat partnership). When boats are:
        // indexable={!delisted && boat.has_owner_text} — a boat with only the
        // marketplace boilerplate has nothing of its own to say, as on riviera.
        >
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [boatsCrumb(locale), { name: title }]))}
        </script>
        <script id={prefetchElementId('boat', boat.id)} type="application/json">
          {serializePrefetch(boat)}
        </script>
      </PageHead>

      <DetailLayout
        crumbs={[
          { label: t('nav.home'), href: localePath(locale) },
          { label: t('nav.boats'), href: localePath(locale, 'boats') },
        ]}
        imageUrl={boat.image_url}
        imageAlt={title}
        category={type || t('card.boat')}
        title={title}
        facts={facts}
        action={
          delisted || !boat.booking_url ? (
            <p>{t('detail.delistedBoat')}</p>
          ) : (
            <>
              <BookLink
                url={boat.booking_url}
                label={t('detail.bookBoat')}
                meta={{ kind: 'boat', source: boat.source, id: boat.id, title, city: boat.city_id, locale }}
              />
              {partner ? <small>{t('detail.bookOn', { partner })}</small> : null}
            </>
          )
        }>
        {description
          .split(/\n{2,}/)
          .filter(Boolean)
          .map((para, i) => (
            <p key={i}>{para}</p>
          ))}
      </DetailLayout>
    </Page>
  );
}
